import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import ReactCountryFlag from 'react-country-flag';

const OAuthKeySelection = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [oauthParams, setOauthParams] = useState(null);
  const [appName, setAppName] = useState('');
  const [approving, setApproving] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  const fetchApiKeys = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        // Not authenticated, redirect to login with return URL
        const currentUrl = encodeURIComponent(window.location.pathname + window.location.search);
        navigate(`/login?return=${currentUrl}`);
        return;
      }

      const response = await fetch('/api/api-keys', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch API keys');
      }

      const data = await response.json();
      setApiKeys(data.keys || []);
    } catch (err) {
      setError(err.message || 'Error loading API keys');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    // Parse OAuth params from URL or state
    const params = new URLSearchParams(location.search);
    const clientId = params.get('client_id');
    const redirectUri = params.get('redirect_uri');
    const state = params.get('state');
    const responseType = params.get('response_type');
    const appNameParam = params.get('app_name');
    
    if (appNameParam) {
      setAppName(appNameParam);
    }

    if (clientId && redirectUri) {
      setOauthParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        state: state,
        response_type: responseType || 'code',
      });
    } else {
      setError("Missing required OAuth parameters");
    }

    // Fetch API keys
    fetchApiKeys();
  }, [location, fetchApiKeys]);

  const handleApprove = async () => {
    if (!selectedKey || !oauthParams) return;
    
    setApproving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/mcp-oauth/approve', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tenant_id: selectedKey.tenantId,
          oauth_params: oauthParams,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to approve authorization');
      }

      const data = await response.json();
      // Redirect to the URL provided by the server
      window.location.href = data.redirect_url;
    } catch (err) {
      setError(err.message || 'Error approving authorization');
      setApproving(false);
    }
  };

  const handleCancel = () => {
    // Redirect back to the redirect_uri with an error
    if (oauthParams?.redirect_uri) {
      const redirectUrl = new URL(oauthParams.redirect_uri);
      redirectUrl.searchParams.append('error', 'access_denied');
      if (oauthParams.state) {
        redirectUrl.searchParams.append('state', oauthParams.state);
      }
      window.location.href = redirectUrl.toString();
    } else {
      // If we don't have redirect_uri, just go back to dashboard
      navigate('/dashboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent-purple mx-auto"></div>
          <p className="mt-4 text-text">Loading your API keys...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="max-w-4xl w-full px-4">
        <div className="bg-component-bg p-8 rounded-xl shadow-lg border border-gray-800">
          <h1 className="text-2xl font-bold text-heading-text mb-6">
            Authorize Access to Your HPKV API Key
          </h1>
          
          {error && (
            <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-sm text-red-500">{error}</p>
            </div>
          )}

          <div className="mb-6">
            <p className="text-text mb-4">
              <strong className="text-accent-purple">{appName || 'An application'}</strong> is requesting access to one of your HPKV API keys. 
              Select which key you want to authorize:
            </p>
            
            {apiKeys.length === 0 ? (
              <div className="flex items-center p-4 bg-yellow-900/20 rounded-lg mb-4">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mr-2" />
                <p className="text-yellow-500">
                  You don&apos;t have any API keys yet. Please go to the <a href="/api-keys" className="underline">API Keys page</a> to create one first.
                </p>
              </div>
            ) : (
              <div className="space-y-4 mt-4">
                {apiKeys.map((key) => (
                  <div 
                    key={key.tenantId}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedKey?.tenantId === key.tenantId 
                        ? 'border-accent-purple bg-accent-purple/10' 
                        : 'border-gray-700 hover:border-accent-purple/50'
                    }`}
                    onClick={() => setSelectedKey(key)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="mr-3">
                          <div className="w-5 h-5 rounded-full border-2 border-gray-600 flex items-center justify-center">
                            {selectedKey?.tenantId === key.tenantId && (
                              <div className="w-3 h-3 rounded-full bg-accent-purple"></div>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="font-semibold text-heading-text mb-1">
                            {key.description || 'HPKV API Key'}
                          </div>
                          <div className="text-sm text-text flex items-center">
                            <ReactCountryFlag 
                              countryCode={key.country_code} 
                              svg 
                              style={{
                                width: '1em',
                                height: '1em',
                                marginRight: '0.5em'
                              }}
                            />
                            {key.location}
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-400">
                        {new Date(key.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-between mt-8">
            <button
              onClick={handleCancel}
              className="px-6 py-3 rounded-lg bg-gray-800 hover:bg-gray-700 text-text transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApprove}
              disabled={!selectedKey || approving || apiKeys.length === 0}
              className="px-6 py-3 rounded-lg bg-accent-purple text-white hover:bg-accent-blue transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {approving ? 'Approving...' : 'Approve'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OAuthKeySelection; 